import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Table,
  Tag,
  Drawer,
  Input,
  Select,
  Space,
  Button,
  Modal,
  message,
  Empty,
  Tooltip,
} from "antd";
import {
  CaretRightOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import PropTypes from "prop-types";
import PageHeader from "../Layout/PageHeader";
import { formatDateTime } from "../../helpers/date";
import { formatAmount } from "../../helpers/billingStatement";
import {
  BILLING_NOTE_STATUSES,
  BILLING_NOTE_STATUS_COLORS,
  BILLING_NOTE_REASON_LABELS,
} from "../../constants/billingNote";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const BillingNoteList = ({ noteType, title, apiBase, merchantId, refreshSignal, children, showHeader = true }) => {
  const compact = !!merchantId;

  const [pagination, setPagination] = useState({ currentPage: 1, limit: compact ? 100 : 20 });
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState(null);
  const [selectedCharges, setSelectedCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [archiving, setArchiving] = useState(false);

  const buildFilterParams = useCallback(
    () => ({
      ...(merchantId ? { merchantId } : filters.merchantId && { merchantId: filters.merchantId }),
      ...(filters.status && { status: filters.status }),
    }),
    [filters, merchantId],
  );

  const fetchItems = useCallback(async () => {
    if (compact && !merchantId) return;

    setLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        page: pagination.currentPage,
        limit: pagination.limit,
      };
      const res = await axios.get(apiBase, { params });
      setItems(res.data.data);
      setPagination((prev) => ({ ...prev, totalSize: res.data.pagination?.totalSize }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, compact, merchantId, pagination.currentPage, pagination.limit, buildFilterParams]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems, refreshSignal]);

  useEffect(() => {
    if (!selected) {
      setSelectedCharges([]);
      return;
    }

    setChargesLoading(true);
    axios
      .get(`${apiBase}/${selected.objectId}`)
      .then((res) => setSelectedCharges(res.data.charges || []))
      .catch((error) => message.error(error.response?.data?.message || error.message))
      .finally(() => setChargesLoading(false));
  }, [apiBase, selected]);

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const handleFilterChange = (updater) => {
    setFilters(updater);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const clearAllFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleArchive = () => {
    if (selectedRowKeys.length === 0) return;

    Modal.confirm({
      title: `Archive ${selectedRowKeys.length} ${title.toLowerCase()}?`,
      icon: <ExclamationCircleOutlined />,
      content: "This permanently deletes the selected notes. This cannot be undone.",
      okText: "Archive",
      okButtonProps: { danger: true },
      onOk: async () => {
        setArchiving(true);
        try {
          await axios.post(`${apiBase}/archive`, { ids: selectedRowKeys });
          message.success(`${selectedRowKeys.length} ${title.toLowerCase()} archived`);
          setSelectedRowKeys([]);
          fetchItems();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        } finally {
          setArchiving(false);
        }
      },
    });
  };

  const downloadNotePdf = (noteId) => {
    axios
      .get(`${apiBase}/${noteId}/pdf`)
      .then((res) => window.open(res.data.url))
      .catch((error) => message.error(error.response?.data?.message || error.message));
  };

  // Unlike the PDF, only notes created via the Excel bulk import (BillingNoteImport.js) have
  // one of these -- the API returns a 404 message for any other note, shown the same way as
  // any other action error here.
  const downloadNoteExcel = (noteId) => {
    axios
      .get(`${apiBase}/${noteId}/excel`)
      .then((res) => window.open(res.data.url))
      .catch((error) => message.error(error.response?.data?.message || error.message));
  };

  const actionsFor = (record) => (
    <Space size={4}>
      <Tooltip title="View">
        <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setSelected(record)} />
      </Tooltip>
      <Tooltip title="Download PDF">
        <Button type="text" size="small" icon={<FilePdfOutlined />} onClick={() => downloadNotePdf(record.objectId)} />
      </Tooltip>
      <Tooltip title="Download Excel">
        <Button
          type="text"
          size="small"
          icon={<FileExcelOutlined />}
          onClick={() => downloadNoteExcel(record.objectId)}
        />
      </Tooltip>
    </Space>
  );

  const columns = [
    {
      title: "Document No.",
      dataIndex: "documentNumber",
      key: "documentNumber",
      width: 160,
    },
    ...(compact
      ? []
      : [
        {
          title: "Merchant",
          key: "merchant",
          width: 200,
          ellipsis: true,
          render: (_, r) => r.merchant?.name || r.merchant?.objectId || "-",
        },
        {
          title: "User Email",
          dataIndex: "userEmail",
          key: "userEmail",
          width: 220,
          ellipsis: true,
          render: (v) => v || "-",
        },
      ]),
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      width: 180,
      render: (r) => BILLING_NOTE_REASON_LABELS[r] || r || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (s) => <Tag color={BILLING_NOTE_STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Original Amount",
      key: "originalAmount",
      width: 150,
      align: "right",
      render: (_, r) => formatAmount(r.originalAmount, r.currency),
    },
    {
      title: "Applied",
      key: "appliedAmount",
      width: 130,
      align: "right",
      render: (_, r) => formatAmount(r.appliedAmount, r.currency),
    },
    {
      title: "Remaining",
      key: "remainingBalance",
      width: 130,
      align: "right",
      render: (_, r) => (
        <span style={{ fontWeight: 500, color: Number(r.remainingBalance) > 0 ? "#cf1322" : undefined }}>
          {formatAmount(r.remainingBalance, r.currency)}
        </span>
      ),
    },
    {
      title: "Issued At",
      dataIndex: "issuedAt",
      key: "issuedAt",
      width: 160,
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => actionsFor(record),
    },
  ];

  return (
    <div>
      {!compact && showHeader && (
        <PageHeader title={title} childRoutes={[{ path: apiBase, breadcrumbName: title }]} />
      )}

      {children}

      {!compact && (
        <Collapse
          defaultActiveKey={activeFilterCount > 0 ? ["filters"] : undefined}
          expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        >
          <Collapse.Panel
            key="filters"
            header={
              <Space>
                <span>Filters</span>
                {activeFilterCount > 0 && <Tag color="blue">{activeFilterCount} active</Tag>}
              </Space>
            }
            extra={
              activeFilterCount > 0 && (
                <a onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}>Clear all</a>
              )
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <span>Merchant Id</span>
                  <Input
                    allowClear
                    placeholder="Merchant objectId"
                    value={filters.merchantId || ""}
                    onChange={(e) =>
                      handleFilterChange((f) => ({ ...f, merchantId: e.target.value.trim() || undefined }))
                    }
                  />
                </Space>
              </Col>
              <Col xs={24} sm={8}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <span>Status</span>
                  <Select
                    allowClear
                    placeholder="ALL"
                    style={{ width: "100%" }}
                    value={filters.status}
                    onChange={(v) => handleFilterChange((f) => ({ ...f, status: v }))}
                  >
                    {BILLING_NOTE_STATUSES.map((status) => (
                      <Select.Option key={status} value={status}>
                        {status}
                      </Select.Option>
                    ))}
                  </Select>
                </Space>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>
      )}

      {!compact && (
        <Row justify="end" style={{ marginTop: 16 }}>
          <Button
            danger
            disabled={selectedRowKeys.length === 0}
            loading={archiving}
            onClick={handleArchive}
          >
            Archive Selected ({selectedRowKeys.length})
          </Button>
        </Row>
      )}

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={activeFilterCount > 0 ? `No ${title.toLowerCase()} match these filters` : `No ${title.toLowerCase()} yet`}
              style={{ padding: "24px 0" }}
            >
              {activeFilterCount > 0 && <a onClick={clearAllFilters}>Clear filters</a>}
            </Empty>
          ),
        }}
        style={{ marginTop: 16 }}
        rowSelection={
          compact
            ? undefined
            : { selectedRowKeys, onChange: setSelectedRowKeys }
        }
      />

      {!compact && (
        <Row justify="end" style={{ marginTop: 16 }}>
          <Col>
            <Pagination
              total={pagination?.totalSize}
              pageSize={pagination.limit}
              current={pagination.currentPage}
              showSizeChanger
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onChange={handlePageChange}
            />
          </Col>
        </Row>
      )}

      <Drawer title={`${title} detail`} visible={!!selected} onClose={() => setSelected(null)} width={720}>
        {selected && (
          <div>
            <p>
              <strong>Document No.:</strong> {selected.documentNumber}
            </p>
            <p>
              <strong>Merchant:</strong> {selected.merchant?.name || selected.merchant?.objectId}
            </p>
            <p>
              <strong>Reason:</strong> {BILLING_NOTE_REASON_LABELS[selected.reason] || selected.reason}
            </p>
            <p>
              <strong>Remark:</strong> {selected.remark || "-"}
            </p>
            <p>
              <strong>Status:</strong> <Tag color={BILLING_NOTE_STATUS_COLORS[selected.status]}>{selected.status}</Tag>
            </p>
            <p>
              <strong>Original Amount:</strong> {formatAmount(selected.originalAmount, selected.currency)}
            </p>
            <p>
              <strong>Applied Amount:</strong> {formatAmount(selected.appliedAmount, selected.currency)}
            </p>
            <p>
              <strong>Remaining Balance:</strong> {formatAmount(selected.remainingBalance, selected.currency)}
            </p>
            <p>
              <strong>Issued At:</strong> {selected.issuedAt ? formatDateTime(selected.issuedAt) : "-"}
            </p>

            <Button style={{ marginBottom: 16, marginRight: 8 }} onClick={() => downloadNotePdf(selected.objectId)}>
              Download PDF
            </Button>

            <h4 style={{ marginTop: 24 }}>
              <strong>Attached Charges</strong>
            </h4>
            <Table
              rowKey="objectId"
              dataSource={selectedCharges}
              loading={chargesLoading}
              pagination={false}
              size="small"
              locale={{ emptyText: "No charges attached (manual amount only)" }}
              columns={[
                { title: "Consign No.", dataIndex: "consignNo", key: "consignNo" },
                { title: "Charge Type", dataIndex: "chargeType", key: "chargeType" },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  render: (v) => formatAmount(v, selected.currency),
                },
              ]}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

BillingNoteList.propTypes = {
  noteType: PropTypes.oneOf(["CREDIT", "DEBIT"]).isRequired,
  title: PropTypes.string.isRequired,
  apiBase: PropTypes.string.isRequired,
  merchantId: PropTypes.string,
  refreshSignal: PropTypes.any,
  children: PropTypes.node,
  showHeader: PropTypes.bool,
};

export default BillingNoteList;
